import { Fragment, useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  CostCheckCellState,
  DictionaryValidationMatrix,
  FixedCostOptionChip,
  ValidationMatrixCell,
  ValidationMatrixRow,
} from "@shared-core/data/cost-engine-validation-types";
import {
  IN_HOUSE_ROUTE_CODE,
  type MatrixRouteSummary,
} from "@shared-core/data/fulfilment-route-types";
import {
  formatErroredLeafCount,
  formatVariantResolution,
} from "@shared-core/domain/fulfilment-route-presentation";
import {
  CELL_MARKS,
  CELL_MARK_ORDER,
  IN_HOUSE_ROW_LABEL,
  describeInHouseCell,
  describeSupplierCell,
  formatCheckNowTitle,
  formatDeliveryFootnote,
  formatInHouseCheckCounts,
  formatMatrixCellLabel,
  isColouredMark,
  type MatrixCellDisplay,
} from "@shared-core/domain/cost-matrix-cells";
import {
  fixedCostOptionsKey,
  fixedCostOptionsLabel,
} from "@shared-core/domain/cost-engine-validation-presentation";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { ListPagination } from "../../patterns/ListPagination";
import { Mark, MarkMessage } from "./MarkMessage";
import { Loader2, Search } from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

// Cost-check-classified variant matrix (product brief
// docs/features/cost-engine-productionisation/01_product_brief.md §8,
// journey 2a; review decisions in 02_glossary_and_live_costing.md) — the
// item page's primary "am I confident in this price" surface. Variant
// search, cost-check filter and paging are pushed down to the adapter
// (docs/list-endpoints.md), not filtered client-side.
//
// Fulfilment routes (catalogue third-party fulfilment brief §8, journey A):
// each variant is a row group — one "In-house" row, then a row per supplier
// route mapped for the item. Routes stack as rows so a make-vs-buy
// comparison at one quantity reads straight down a column. No route is
// marked as the default.
//
// Layout (Ben, 24 Sep): one key, one line, four marks meaning the same on
// every row type; every cell is ONE figure then "mark · message" and nothing
// else. An in-house cell shows the checked figure and the check's verdict
// whenever a check exists (so the grid and the cost-pair page can never
// disagree), else the engine's cost, grey; the cached Tharstern estimate
// and its age while paused. Only a coloured cell links to the cost-pair
// page. Checking is asked for through a small popover (a cell's Check now,
// a row's Check all), the only place weekly-check membership appears.

const PAGE_SIZE = 20;

/** Why a Check now failed, in the words the user can act on. 429 (budget
 *  exhausted) and 502 (Tharstern unavailable) are the two errors
 *  `requestCellCheck` itself throws (cost-engine-productionisation spec 03
 *  §5.3, spec 07 §6). */
function checkFailureMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    return (
      err.detail || "This week's check budget is used up. Try again next week."
    );
  }
  if (err instanceof ApiError && err.status === 502) {
    return (
      err.detail || "Tharstern is unavailable right now. Try again shortly."
    );
  }
  return "Check now failed. Try again.";
}

export function ValidationCostMatrix({
  dictionaryItemId,
}: {
  dictionaryItemId: string;
}) {
  const adapter = useDataAdapter();
  const [matrix, setMatrix] = useState<DictionaryValidationMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkingKeys, setCheckingKeys] = useState<ReadonlySet<string>>(
    new Set(),
  );
  // A failed check leaves its cells as they were; the failure is said once,
  // above the table, until the next check.
  const [checkError, setCheckError] = useState<string | null>(null);

  const [variantQuery, setVariantQuery] = useState("");
  const debouncedVariantQuery = useDebouncedValue(variantQuery, 300);
  const [costCheckFilter, setCostCheckFilter] = useState<
    CostCheckCellState | ""
  >("");
  const [page, setPage] = useState(1);
  // A cached estimate's cell says how old it is (decision 75).
  const now = currentTime();

  // A changed filter always starts back at page 1 — a stale page number
  // from a wider result set could otherwise land past the end of a
  // narrower one.
  useEffect(() => {
    setPage(1);
  }, [debouncedVariantQuery, costCheckFilter, dictionaryItemId]);

  const load = useCallback(() => {
    setLoadError(null);
    return adapter
      .getDictionaryValidationMatrix(dictionaryItemId, {
        variantQuery: debouncedVariantQuery || undefined,
        costCheckState: costCheckFilter || undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      .then(setMatrix)
      .catch(() => setLoadError("Failed to load the validation matrix"))
      .finally(() => setLoading(false));
  }, [adapter, dictionaryItemId, debouncedVariantQuery, costCheckFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // A fixed-cost corner row repeats its variant id, so every per-cell key
  // carries the row's options too.
  const cellKey = (
    variantId: string,
    quantity: number,
    fixedCostOptions: readonly FixedCostOptionChip[],
  ) => `${variantId}:${quantity}:${fixedCostOptionsKey(fixedCostOptions)}`;

  /** Check the given cells now and, when the popover's "Also check weekly"
   *  was changed, bring each cell's weekly membership into line with it. */
  async function checkCells(
    row: ValidationMatrixRow,
    cells: readonly ValidationMatrixCell[],
    alsoWeekly: boolean,
    wasWeekly: boolean,
  ) {
    const options = row.fixedCostOptions ?? [];
    const keys = cells.map((c) => cellKey(row.variantId, c.quantity, options));
    setCheckError(null);
    setCheckingKeys((prev) => new Set([...prev, ...keys]));
    try {
      if (alsoWeekly !== wasWeekly) {
        await Promise.all(
          cells
            .filter((c) => (c.rollingSetOrigin != null) !== alsoWeekly)
            .map((c) =>
              adapter.setCellRollingValidationPin(
                dictionaryItemId,
                row.variantId,
                c.quantity,
                alsoWeekly,
                options,
              ),
            ),
        );
      }
      const results = await Promise.allSettled(
        cells.map((c) =>
          adapter.requestCellCheck(
            dictionaryItemId,
            row.variantId,
            c.quantity,
            options,
          ),
        ),
      );
      const failed = results.find(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      if (failed) setCheckError(checkFailureMessage(failed.reason));
    } catch (err) {
      setCheckError(checkFailureMessage(err));
    } finally {
      // Refresh before the cells leave "Checking…": their Check now trigger
      // comes back only once it can show the new weekly membership, never
      // the stale one it had before this check.
      await load();
      setCheckingKeys((prev) => {
        const next = new Set(prev);
        for (const key of keys) next.delete(key);
        return next;
      });
    }
  }

  if (loadError) {
    return <p className="text-xs text-destructive">{loadError}</p>;
  }
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading variant costs…
      </div>
    );
  }
  if (matrix === null) {
    // Not a failure — this dictionary item simply isn't in the check
    // ledger yet (never priced, or outside the costed estate). A calm,
    // neutral state, distinct from an actual load error above
    // (coordinator review round 6).
    return (
      <p className="text-xs text-muted-foreground">
        No cost checks for this item yet.
      </p>
    );
  }

  const totalPages = Math.ceil(matrix.filteredRowCount / PAGE_SIZE);
  const hasSupplierRows = matrix.rows.some((r) => r.supplierRoutes.length > 0);
  const { routes, isMadeInHouse } = matrix;
  const routesByCode = new Map(routes.map((r) => [r.route.code, r] as const));
  const deliveryIncludedRoutes = routes
    .filter(
      (r) =>
        r.route.code !== IN_HOUSE_ROUTE_CODE &&
        r.isDeliveryIncluded &&
        matrix.rows.some((row) =>
          row.supplierRoutes.some((s) => s.routeCode === r.route.code),
        ),
    )
    .map((r) => r.route.label);

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Variant manufacturing costs
        </p>
        <p className="text-sm text-muted-foreground">
          {isMadeInHouse
            ? `${matrix.validatedCellCount} of ${matrix.totalCellCount} prices checked`
            : "Not made in-house — supplier routes only"}
        </p>
      </div>

      <RouteSummary dictionaryItemId={dictionaryItemId} routes={routes} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1 min-w-[10rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search variants..."
            value={variantQuery}
            onChange={(e) => setVariantQuery(e.target.value)}
            className="pl-9 h-8"
            aria-label="Search variants"
          />
        </div>
        {isMadeInHouse && (
          <select
            aria-label="Filter by cost check"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={costCheckFilter}
            onChange={(e) =>
              setCostCheckFilter(e.target.value as CostCheckCellState | "")
            }
          >
            <option value="">All cost checks</option>
            <option value="green">Matches</option>
            <option value="amber">Small differences</option>
            <option value="red">Mismatch</option>
            <option value="grey">Not yet checked</option>
          </select>
        )}
      </div>

      <MatrixKey
        footnote={
          deliveryIncludedRoutes.length > 0
            ? formatDeliveryFootnote(deliveryIncludedRoutes)
            : null
        }
      />

      {checkError && (
        <p role="alert" className="text-xs text-destructive">
          {checkError}
        </p>
      )}

      {matrix.rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {matrix.filteredRowCount === 0 && (variantQuery || costCheckFilter)
            ? "No variants match these filters."
            : "No cost-affecting option combinations for this item."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="sticky left-0 min-w-[16rem] bg-muted/40 px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                  Variant
                </th>
                {hasSupplierRows && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Fulfilment route
                  </th>
                )}
                {matrix.columns.map((quantity) => (
                  <th
                    key={quantity}
                    className="px-3 py-2 text-center text-xs font-medium text-muted-foreground"
                  >
                    {quantity.toLocaleString()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row) => {
                const superseded = row.isActive === false;
                const options = row.fixedCostOptions ?? [];
                const isCorner = options.length > 0;
                const rowKey = `${row.variantId}:${fixedCostOptionsKey(options)}`;
                const variantName = isCorner
                  ? `${row.displayLabel} with ${fixedCostOptionsLabel(options)}`
                  : row.displayLabel;
                // A supplier-only item (#71) has no in-house row.
                const groupSize =
                  (isMadeInHouse ? 1 : 0) + row.supplierRoutes.length;
                const isRowWeekly = row.cells.every(
                  (c) => c.rollingSetOrigin != null,
                );
                const variantCell = (
                  <td
                    rowSpan={groupSize}
                    className="group/variant sticky left-0 border-b border-border bg-background px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {isCorner ? (
                        // A fixed-cost corner: the variant above, checked
                        // with these job-level options switched on.
                        <span className="flex flex-wrap items-center gap-1 pl-3 text-muted-foreground">
                          <span aria-hidden>↳</span>
                          <span className="text-xs">with</span>
                          {options.map((option) => (
                            <span
                              key={option.fieldKey}
                              className="rounded-full border border-border px-1.5 py-0.5 text-[10px] font-medium text-foreground"
                            >
                              {option.label}
                            </span>
                          ))}
                          <span className="sr-only">{variantName}</span>
                        </span>
                      ) : (
                        <span
                          className={superseded ? "text-muted-foreground" : ""}
                        >
                          <span className="font-medium">
                            {row.displayLabel}
                          </span>
                          {superseded && (
                            <span className="ml-1 text-xs">· superseded</span>
                          )}
                        </span>
                      )}
                      {isMadeInHouse && (
                        <CheckNowPopover
                          title={formatCheckNowTitle(variantName, "all")}
                          triggerText="Check all"
                          triggerLabel={`Check all: ${variantName}`}
                          isWeekly={isRowWeekly}
                          revealClassName="group-hover/variant:opacity-100 group-focus-within/variant:opacity-100"
                          onConfirm={(alsoWeekly) =>
                            checkCells(row, row.cells, alsoWeekly, isRowWeekly)
                          }
                        />
                      )}
                    </div>
                  </td>
                );
                return (
                  <Fragment key={rowKey}>
                    {isMadeInHouse && (
                      <tr
                        className={`align-top ${groupSize === 1 ? "border-b border-border last:border-0" : ""}`}
                      >
                        {variantCell}
                        {hasSupplierRows && (
                          <RowLabelCell label={IN_HOUSE_ROW_LABEL} />
                        )}
                        {row.cells.map((cell, index) => {
                          const isChecking = checkingKeys.has(
                            cellKey(row.variantId, cell.quantity, options),
                          );
                          const inHouse = row.inHouse[index];
                          const display = describeInHouseCell(
                            inHouse,
                            isChecking,
                            now,
                          );
                          const label = formatMatrixCellLabel(
                            isCorner
                              ? `${IN_HOUSE_ROW_LABEL} with ${fixedCostOptionsLabel(options)}`
                              : IN_HOUSE_ROW_LABEL,
                            cell.quantity,
                            display,
                          );
                          const isWeekly = cell.rollingSetOrigin != null;
                          return (
                            <td
                              key={cell.quantity}
                              className="group/cell relative px-2 py-2 text-center align-top"
                            >
                              {isColouredMark(display.mark) ? (
                                <Link
                                  to={paths.dictionaryCostPair({
                                    dictionaryItemId,
                                    variantId: row.variantId,
                                    quantity: cell.quantity,
                                    fixedCostOptions: options,
                                  })}
                                  className="block rounded px-2 py-1 transition-opacity hover:opacity-75"
                                  aria-label={label}
                                >
                                  <CellContent display={display} />
                                </Link>
                              ) : (
                                // Nothing to show on the cost-pair page yet —
                                // Check now is the action.
                                <div
                                  role="group"
                                  aria-label={label}
                                  className="px-2 py-1"
                                >
                                  <CellContent display={display} />
                                </div>
                              )}
                              {!isChecking && (
                                <CheckNowPopover
                                  title={formatCheckNowTitle(
                                    variantName,
                                    cell.quantity,
                                  )}
                                  triggerText="Check now"
                                  triggerLabel={`Check now: ${variantName} at ${cell.quantity.toLocaleString()}`}
                                  isWeekly={isWeekly}
                                  revealClassName="absolute right-0.5 top-0.5 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100"
                                  onConfirm={(alsoWeekly) =>
                                    checkCells(
                                      row,
                                      [cell],
                                      alsoWeekly,
                                      isWeekly,
                                    )
                                  }
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                    {row.supplierRoutes.map((supplierRow, index) => {
                      const route = routesByCode.get(supplierRow.routeCode)
                        ?.route ?? {
                        code: supplierRow.routeCode,
                        label: supplierRow.routeCode,
                      };
                      const isLastInGroup =
                        index === row.supplierRoutes.length - 1;
                      return (
                        <tr
                          key={supplierRow.routeCode}
                          className={`align-top bg-muted/20 ${isLastInGroup ? "border-b border-border" : ""}`}
                        >
                          {!isMadeInHouse && index === 0 && variantCell}
                          <RowLabelCell
                            label={route.label}
                            suspension={
                              supplierRow.suspended
                                ? {
                                    reason:
                                      supplierRow.suspensionReason ?? null,
                                  }
                                : null
                            }
                          />
                          {supplierRow.cells.map((cell) => {
                            const display = describeSupplierCell(cell);
                            return (
                              <td
                                key={cell.quantity}
                                className="px-2 py-2 text-center align-top"
                              >
                                <Link
                                  to={paths.dictionaryCostPair({
                                    dictionaryItemId,
                                    variantId: row.variantId,
                                    quantity: cell.quantity,
                                    routeCode: route.code,
                                  })}
                                  className="block rounded px-2 py-1 transition-opacity hover:opacity-75"
                                  aria-label={formatMatrixCellLabel(
                                    route.label,
                                    cell.quantity,
                                    display,
                                  )}
                                >
                                  <CellContent display={display} />
                                </Link>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

// ── Cells and the key ───────────────────────────────────────────────────────

/** The one key: four marks on one line, then the delivery footnote. */
function MatrixKey({ footnote }: { footnote: string | null }) {
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground">
      <p aria-label="Key" className="flex flex-wrap gap-x-4 gap-y-1">
        {CELL_MARK_ORDER.map((mark) => (
          <span key={mark} className="inline-flex items-center gap-1">
            <Mark mark={mark} />
            {CELL_MARKS[mark].label} · {CELL_MARKS[mark].meaning}
          </span>
        ))}
      </p>
      {footnote && <p>{footnote}</p>}
    </div>
  );
}

/**
 * A route's row header. A suspended supplier route is marked "Suspended"
 * (spec 11 §3.3), with the reason in the tooltip; its cells keep their
 * figures and marks, because suspension stops the route being offered, not
 * its quotes. (A design addition to the approved mock, which drew no mark —
 * minimal on purpose, pending review.)
 */
function RowLabelCell({
  label,
  suspension = null,
}: {
  label: string;
  suspension?: { reason: string | null } | null;
}) {
  return (
    <td className="whitespace-nowrap px-3 py-2 text-xs font-medium text-muted-foreground">
      <span className="flex items-center gap-1.5">
        {label}
        {suspension && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="warning"
                tabIndex={0}
                className="cursor-help"
                aria-label={
                  suspension.reason
                    ? `Suspended: ${suspension.reason}`
                    : "Suspended"
                }
              >
                Suspended
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {suspension.reason
                ? `Suspended: ${suspension.reason}. Not offered on any quote until it is resumed.`
                : "Suspended. Not offered on any quote until it is resumed."}
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </td>
  );
}

/** Line 1 the figure (or "—"), line 2 the mark and its message. */
function CellContent({ display }: { display: MatrixCellDisplay }) {
  return (
    <>
      <span className="block tabular-nums text-foreground">
        {display.figure}
      </span>
      <MarkMessage
        mark={display.mark}
        message={display.message}
        className="mt-0.5 justify-center text-[11px] text-muted-foreground"
      />
    </>
  );
}

/** Check now / Check all: a small popover asking to confirm, with "Also
 *  check weekly" reflecting — and able to change — whether the cell (or
 *  every cell in the row) is in the weekly set. The trigger shows on hover
 *  and focus; Escape or a click away cancels. */
function CheckNowPopover({
  title,
  triggerText,
  triggerLabel,
  isWeekly,
  revealClassName,
  onConfirm,
}: {
  title: string;
  triggerText: string;
  triggerLabel: string;
  isWeekly: boolean;
  /** Where the trigger sits and when it is revealed (hover/focus). */
  revealClassName: string;
  onConfirm: (alsoWeekly: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [alsoWeekly, setAlsoWeekly] = useState(isWeekly);
  const checkboxId = useId();
  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => {
        // Every opening starts from the current membership.
        if (next) setAlsoWeekly(isWeekly);
        setIsOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className={`shrink-0 rounded px-1 text-[10px] font-medium text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 data-[state=open]:opacity-100 ${revealClassName}`}
        >
          {triggerText}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3 p-3">
        <p className="text-sm font-medium">{title}</p>
        <div className="flex items-center gap-2">
          <Checkbox
            id={checkboxId}
            checked={alsoWeekly}
            onCheckedChange={(value) => setAlsoWeekly(value === true)}
          />
          <label htmlFor={checkboxId} className="text-sm">
            Also check weekly
          </label>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setIsOpen(false);
            onConfirm(alsoWeekly);
          }}
        >
          Check now
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// ── Fulfilment routes ───────────────────────────────────────────────────────

/** What each route is and where it stands for this item: in-house costed
 *  from Tharstern estimates, each supplier route mapped or not. Each route's mapping error
 *  count links to the item's Mappings tab. Side by side for comparison
 *  only — no route is the default until the sourcing policy arrives. */
function RouteSummary({
  dictionaryItemId,
  routes,
}: {
  dictionaryItemId: string;
  routes: MatrixRouteSummary[];
}) {
  return (
    <div className="space-y-1 rounded-md border border-border px-3 py-2 text-xs">
      <p className="font-medium uppercase tracking-wide text-muted-foreground">
        Fulfilment routes
      </p>
      <ul className="space-y-0.5">
        {routes.map((summary) => (
          <li key={summary.route.code} className="text-muted-foreground">
            <span className="font-medium text-foreground">
              {summary.route.label}
            </span>
            {" — "}
            {summary.route.code === IN_HOUSE_ROUTE_CODE ? (
              <>
                Costed from Tharstern estimates ·{" "}
                <span
                  className={
                    (summary.checks?.substitutions ?? 0) > 0
                      ? "text-destructive"
                      : undefined
                  }
                >
                  {summary.checks
                    ? formatInHouseCheckCounts(summary.checks)
                    : "No checks yet"}
                </span>
              </>
            ) : !summary.isConfigured ? (
              "not set up in this environment, so no quotes are fetched"
            ) : summary.mapping == null ? (
              "not mapped for this item"
            ) : (
              <>
                trade-tier buy cost, net
                {summary.isDeliveryIncluded ? ", delivery included" : ""} ·{" "}
                {summary.turnaroundTier} turnaround · fetched nightly, valid for
                36 hours ·{" "}
                {formatVariantResolution(
                  summary.mapping.resolvedVariantCount,
                  summary.mapping.totalVariantCount,
                )}{" "}
                ·{" "}
                <ErroredLeafCount
                  dictionaryItemId={dictionaryItemId}
                  count={summary.mapping.failingLeafCount}
                />
              </>
            )}
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground">
        Routes are shown side by side for comparison; none is the default yet.
      </p>
    </div>
  );
}

/** "1 mapping errored last night", linking to the item's Mappings tab. */
function ErroredLeafCount({
  dictionaryItemId,
  count,
}: {
  dictionaryItemId: string;
  count: number;
}) {
  return (
    <Link
      to={paths.dictionaryItemMappings({ dictionaryItemId })}
      className={`underline underline-offset-2 ${count > 0 ? "text-destructive" : ""}`}
    >
      {formatErroredLeafCount(count)}
    </Link>
  );
}
